// Shared skeleton placeholders for abilities pages.
//
// These are imported BY the route-level `loading.tsx` files AND rendered inline
// by the page's client-side loading branch. Without the inline render, the page
// flashes blank between Next.js handing off from loading.tsx and the client
// useEffect resolving — because the client component mounts immediately, the
// loading.tsx Suspense boundary releases, and `return null` paints empty.

// ---------------------------------------------------------------------------
// Inside-ability skeleton — structure-true twin of the editorial /abilities/
// [slug] layout (abd-*): hero, colonnade, two card chapters. Static bars, no
// shimmer, and varied widths so the cold frame reads as a page rather than a
// loading pattern.
// ---------------------------------------------------------------------------

/** One static bar. `w` accepts a percentage string for fluid rows. */
function Bone({ w, h, r = 6 }: { w: number | string; h: number; r?: number }) {
  return (
    <span
      className="abd-bone block bg-bg-muted rounded-8"
      style={{ width: w, height: h, borderRadius: r }}
    />
  );
}

function CardBone({ desc }: { desc: string }) {
  return (
    <div className="abd-card relative flex-1 min-w-0 text-left font-sans text-[inherit] no-underline bg-bg-elevated border border-border-default rounded-20 pt-6 px-6.5 pb-4.5 cursor-pointer overflow-hidden flex flex-col transition-colors after:[content:''] after:absolute after:-top-22.5 after:-right-22.5 after:w-85 after:h-70 after:bg-gold-glow after:pointer-events-none hover:border-gold-line-34 hover:[&_.abd-card\_\_go]:opacity-100 can-hover:[&:focus-visible_.abd-card\_\_go]:opacity-100 hover:[&_.lib-card\_\_remove]:opacity-100 can-hover:[&:focus-within_.lib-card\_\_remove]:opacity-100 cursor-default">
      <span className="abd-card__top flex items-center gap-3.5">
        <span className="abd-card__chip w-10 h-10 rounded-12 bg-gold-molten-wash border border-solid border-gold-molten-mid-16 flex items-center justify-center shrink-0">
          <Bone w={19} h={19} r={5} />
        </span>
        <Bone w={124} h={15} />
      </span>
      <span className="abd-card__desc text-3.5 leading-[1.65] text-fg-subtle mt-3.5 flex-1 line-clamp-2 block">
        <Bone w={desc} h={12} r={4} />
        <span className="block h-[7px]" />
        <Bone w="58%" h={12} r={4} />
      </span>
      <span className="abd-card__foot flex items-center justify-between gap-2.5 mt-4 pt-3.5 border-t border-border-subtle">
        <Bone w={92} h={10} r={3} />
      </span>
    </div>
  );
}

export function InsideAbilitySkeleton() {
  return (
    <div className="abd [--abd-amber:color-mix(in_srgb,_var(--color-amber)_86%,_var(--fg-default))] contents">
      <div className="flex items-center">
        <Bone w={78} h={13} r={4} />
      </div>

      <div className="flex items-start gap-6 max-sm:flex-wrap max-sm:gap-4">
        <div className="abd-hero__icon w-17 h-17 rounded-20 bg-gold-molten-wash border border-solid border-gold-molten-mid-18 flex items-center justify-center shrink-0 -mt-1.5 max-sm:w-13 max-sm:h-13 max-sm:rounded-16 max-sm:-mt-2">
          <Bone w={32} h={32} r={9} />
        </div>
        <div className="flex-1 min-w-0">
          <Bone w="42%" h={46} r={12} />
        </div>
      </div>

      <div className="text-4 leading-[1.75] text-fg-muted">
        <Bone w="92%" h={14} r={4} />
        <span className="block h-[9px]" />
        <Bone w="84%" h={14} r={4} />
        <span className="block h-[9px]" />
        <Bone w="46%" h={14} r={4} />
      </div>

      <div className="abd-colonnade flex flex-wrap border-t border-border-default border-b border-border-default">
        {[34, 26, 30, 62, 74].map((w, i) => (
          <div
            key={i}
            className="[flex:1_1_0] min-w-32.5 pt-4 px-6 pb-4.5 border-l border-border-subtle first:[border-left:none] first:pl-0.5 max-sm:min-w-[40%] max-sm:pt-3 max-sm:px-4 max-sm:pb-3.5 max-sm:nth-child(odd):[border-left:none] max-sm:nth-child(odd):pl-0.5"
          >
            <Bone w={i === 4 ? 52 : 40} h={9} r={3} />
            <span className="block h-[8px]" />
            <Bone w={w} h={13} r={4} />
          </div>
        ))}
      </div>

      <div className="">
        <div className="flex items-baseline gap-3 mb-3.5">
          <Bone w={122} h={24} r={7} />
          <span className="flex-1 h-px bg-border-default transform-[translateY(-6px)]" />
        </div>
        <div className="abd-grid grid grid-cols-3 gap-3 sm:gap-4 max-lg:grid-cols-2 max-sm:grid-cols-[1fr]">
          {["86%", "74%", "92%"].map((desc, i) => (
            <CardBone key={i} desc={desc} />
          ))}
        </div>
      </div>

      <div className="">
        <div className="flex items-baseline gap-3 mb-3.5">
          <Bone w={132} h={24} r={7} />
          <span className="flex-1 h-px bg-border-default transform-[translateY(-6px)]" />
        </div>
        <div className="abd-grid grid grid-cols-3 gap-3 sm:gap-4 max-lg:grid-cols-2 max-sm:grid-cols-[1fr]">
          <CardBone desc="78%" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill drill-in skeleton — structure-true twin of the dossier (abd-*): hero,
// lead, then the shelf beside the open file. Same scroller and same dock
// reservation as the page it precedes, so nothing shifts when it is replaced.
// ---------------------------------------------------------------------------

export function SkillDrillInSkeleton() {
  return (
    <div className="abd [--abd-amber:color-mix(in_srgb,_var(--color-amber)_86%,_var(--fg-default))] contents">
      <div className="flex items-center">
        <Bone w={96} h={13} r={4} />
      </div>

      <div className="abd-hero--drill flex items-start max-sm:flex-wrap gap-3 sm:gap-4 [&_.abd-hero\_\_icon]:w-12 [&_.abd-hero\_\_icon]:h-12 [&_.abd-hero\_\_icon]:rounded-16 [&_.abd-hero\_\_icon]:-mt-px [&_.abd-hero\_\_title]:text-11 [&_.abd-hero\_\_title]:leading-[1.1] max-sm:gap-3.5 max-sm:[&_.abd-hero\_\_icon]:w-10 max-sm:[&_.abd-hero\_\_icon]:h-10 max-sm:[&_.abd-hero\_\_icon]:rounded-12 max-sm:[&_.abd-hero\_\_icon]:mt-1 max-sm:[&_.abd-hero\_\_title]:text-8">
        <div className="abd-hero__icon w-17 h-17 rounded-20 bg-gold-molten-wash border border-solid border-gold-molten-mid-18 flex items-center justify-center shrink-0 -mt-1.5 max-sm:w-13 max-sm:h-13 max-sm:rounded-16 max-sm:-mt-2">
          <Bone w={24} h={24} r={7} />
        </div>
        <div className="flex-1 min-w-0">
          <Bone w="38%" h={36} r={10} />
          <div className="flex items-baseline flex-wrap gap-2.5 mt-2.5">
            <Bone w={184} h={13} r={4} />
            <Bone w={128} h={11} r={4} />
          </div>
        </div>
      </div>

      <div className="text-4 leading-[1.75] text-fg-muted">
        <Bone w="94%" h={14} r={4} />
        <span className="block h-[9px]" />
        <Bone w="61%" h={14} r={4} />
      </div>

      <div className="abd-dossier grid grid-cols-[250px_1fr] gap-14 mt-10 items-[start] max-sm:grid-cols-[1fr] max-sm:gap-5 max-sm:mt-7.5">
        <div className="abd-shelf sticky top-6 max-sm:static max-sm:flex max-sm:overflow-x-auto max-sm:gap-1 max-sm:pb-1">
          <div className="font-mono text-2.5 tracking-[0.09em] uppercase text-fg-subtle pt-0 px-2.5 pb-2.5 max-sm:hidden">
            <Bone w={52} h={9} r={3} />
          </div>
          {[
            { dir: false, w: 76 },
            { dir: false, w: 88 },
            { dir: true, w: 68 },
            { dir: false, w: 108 },
            { dir: false, w: 92 },
            { dir: true, w: 62 },
            { dir: false, w: 84 },
          ].map((row, i) => (
            <div
              key={i}
              className={
                row.dir
                  ? "font-mono text-3 tracking-wider text-fg-subtle pt-3 px-2.5 pb-1 pl-[calc(10px_+_var(--abd-depth,_0)_*_12px)] max-sm:hidden"
                  : "flex items-center gap-2 w-full text-left bg-none border-none [border-left:2px_solid_transparent] font-sans text-3.5 text-fg-muted py-2 px-2.5 pl-[calc(10px_+_var(--abd-depth,_0)_*_12px)] cursor-pointer transition-colors hover:text-fg-default hover:bg-bg-subtle [&_svg]:w-3 [&_svg]:h-3 [&_svg]:[stroke:currentColor] [&_svg]:opacity-56 [&_svg]:shrink-0 [&_span]:overflow-hidden [&_span]:text-ellipsis [&_span]:whitespace-nowrap max-sm:[flex:0_0_auto] max-sm:w-auto max-sm:border border-border-default max-sm:rounded-100 max-sm:py-1.5 px-3"
              }
            >
              <Bone w={row.w} h={row.dir ? 9 : 12} r={row.dir ? 3 : 4} />
            </div>
          ))}
        </div>

        <div className="abd-doc min-w-0">
          <div className="font-mono text-3 tracking-wider text-fg-subtle pb-3.5 border-b border-border-subtle mb-6.5 [overflow-wrap:anywhere] [&_b]:text-fg-muted [&_b]:font-medium">
            <Bone w={148} h={10} r={3} />
          </div>
          <div className="abd-doc__body flex flex-col gap-2.5">
            {[100, 95, 87, 62, 100, 92, 78].map((pct, i) => (
              <Bone key={i} w={`${pct}%`} h={14} r={4} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent drill-in skeleton — structure-true twin of the character page (abd-*):
// hero, lead, the two pill clusters, then the instructions. Same scroller and
// same dock reservation as the page it precedes, so nothing shifts when it is
// replaced.
// ---------------------------------------------------------------------------

/** One pill-shaped bone, the resting shape of a capability. */
function PillBone({ w }: { w: number }) {
  return (
    <span className="abd-cap inline-flex items-center gap-2 font-sans text-3.5 font-medium text-fg-muted bg-bg-elevated rounded-100 py-2 px-3.5 no-underline border border-transparent">
      <Bone w={w} h={12} r={4} />
    </span>
  );
}

export function AgentDrillInSkeleton() {
  return (
    <div className="abd [--abd-amber:color-mix(in_srgb,_var(--color-amber)_86%,_var(--fg-default))] contents">
      <div className="flex flex-col gap-y-3">
        <div className="flex items-center">
          <Bone w={96} h={13} r={4} />
        </div>

        <div className="abd-hero--drill flex items-start max-sm:flex-wrap gap-4.5 [&_.abd-hero\_\_icon]:w-12 [&_.abd-hero\_\_icon]:h-12 [&_.abd-hero\_\_icon]:rounded-16 [&_.abd-hero\_\_icon]:-mt-px [&_.abd-hero\_\_title]:text-11 [&_.abd-hero\_\_title]:leading-[1.1] max-sm:gap-3.5 max-sm:[&_.abd-hero\_\_icon]:w-10 max-sm:[&_.abd-hero\_\_icon]:h-10 max-sm:[&_.abd-hero\_\_icon]:rounded-12 max-sm:[&_.abd-hero\_\_icon]:mt-1 max-sm:[&_.abd-hero\_\_title]:text-8">
          <div className="abd-hero__icon w-17 h-17 rounded-20 bg-gold-molten-wash border border-solid border-gold-molten-mid-18 flex items-center justify-center shrink-0 -mt-1.5 max-sm:w-13 max-sm:h-13 max-sm:rounded-16 max-sm:-mt-2">
            <Bone w={22} h={28} r={5} />
          </div>
          <div className="flex-1 min-w-0">
            <Bone w="34%" h={36} r={10} />
            <div className="flex items-baseline flex-wrap gap-2.5 mt-2.5">
              <Bone w={196} h={13} r={4} />
              <Bone w={132} h={11} r={4} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-y-11 sm:gap-y-14">
        <div className="text-4 leading-[1.75] text-fg-muted">
          <Bone w="93%" h={14} r={4} />
          <span className="block h-[9px]" />
          <Bone w="72%" h={14} r={4} />
        </div>

        <div className="abd-sect">
          <div className="flex items-baseline gap-3 mb-3.5">
            <Bone w={158} h={24} r={7} />
            <span className="flex-1 h-px bg-border-default transform-[translateY(-6px)]" />
          </div>
          <div className="abd-cap-group [&_+_.abd-cap-group]:mt-5">
            <span className="abd-cap-eyebrow block font-mono text-2.5 tracking-[0.09em] uppercase text-fg-subtle mb-2.5">
              <Bone w={38} h={9} r={3} />
            </span>
            <div className="flex flex-wrap gap-2">
              {[74, 92].map((w) => (
                <PillBone key={w} w={w} />
              ))}
            </div>
          </div>
          <div className="abd-cap-group [&_+_.abd-cap-group]:mt-5">
            <span className="abd-cap-eyebrow block font-mono text-2.5 tracking-[0.09em] uppercase text-fg-subtle mb-2.5">
              <Bone w={32} h={9} r={3} />
            </span>
            <div className="flex flex-wrap gap-2">
              {[86, 66, 58, 78].map((w) => (
                <PillBone key={w} w={w} />
              ))}
            </div>
          </div>
        </div>

        <div className="abd-sect">
          <div className="flex items-baseline gap-3 mb-3.5">
            <Bone w={124} h={24} r={7} />
            <span className="flex-1 h-px bg-border-default transform-[translateY(-6px)]" />
          </div>
          <div className="abd-doc__body flex flex-col gap-2.5">
            {[100, 94, 88, 97, 62, 100, 91].map((pct, i) => (
              <Bone key={i} w={`${pct}%`} h={14} r={4} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
